import { useCallback } from "react"

import { useMsal } from "@azure/msal-react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { ChatSessionModel } from "@/api/models"

import { createDocumentApi } from "@/api/api"
import { getToken } from "@/api/auth-api"
import { addMemoryApi, addPrismNoteApi } from "@/api/memory"
import { useRoutes } from "@/contexts/use-routes"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { usePendingMessageStore } from "@/stores/pending-message-store"
import { type SessionType } from "@/types"

export type ContextMenuAction =
  | "ask_about"
  | "web_search"
  | "web_search_trusted"
  | "create_document"
  | "start_research"
  | "fact_check"
  | "break_out_chat"
  | "add_to_memory"

type UseContextMenuActionsProps = {
  currentSessionId: string | null
  onSendQuestion?: (params: {
    question: string
    startResearch?: boolean
    forceWebSearch?: boolean
  }) => Promise<void>
  onSetQuestion?: (question: string) => void
  onSetAskAboutText?: (text: string) => void
  sessionType?: SessionType
  projectId?: string
}

/**
 * Hook to handle all context menu actions
 * Integrates with existing tool APIs and creates new chat sessions
 */
export function useContextMenuActions({
  currentSessionId,
  onSendQuestion,
  onSetQuestion,
  onSetAskAboutText,
  sessionType = "open",
  projectId,
}: UseContextMenuActionsProps) {
  const navigate = useNavigate()
  const prismMemory = usePrismMemory()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { instance } = useMsal()
  const { getRouteChatPath } = useRoutes()
  const chatService = ChatService.getInstance()
  const setPendingMessage = usePendingMessageStore((state) => state.setPendingMessage)
  const canUseWebSearch = useWebSearchEnabled()

  const logAction = useCallback(
    (action: ContextMenuAction, text: string) => {
      logger.info("TextSelectionAction", {
        action,
        selectedTextLength: text.length,
        timestamp: Date.now(),
        sessionId: currentSessionId,
      })
    },
    [currentSessionId]
  )

  const handleAskAbout = useCallback(
    (selectedText: string) => {
      logAction("ask_about", selectedText)
      // Show selected text as a pill in chat footer
      if (onSetAskAboutText) {
        onSetAskAboutText(selectedText)
      }
    },
    [logAction, onSetAskAboutText]
  )

  const handleWebSearch = useCallback(
    async (selectedText: string) => {
      if (!canUseWebSearch) {
        logger.info("Blocked contextual web search on locked route", { sessionType })
        return
      }
      logAction("web_search", selectedText)
      try {
        // Send question with web search forced on, prepending context instruction
        if (onSendQuestion) {
          const enhancedQuestion = `collect more information regarding this statement: ${selectedText}`
          await onSendQuestion({ question: enhancedQuestion, forceWebSearch: true })
        }
      } catch (error) {
        logger.error("Failed to execute web search", error as Error)
        toast.error("Search failed", {
          description: "Please try again",
        })
      }
    },
    [canUseWebSearch, logAction, onSendQuestion, sessionType]
  )

  const handleWebSearchTrusted = useCallback(
    async (selectedText: string) => {
      if (!canUseWebSearch) {
        logger.info("Blocked contextual trusted web search on locked route", { sessionType })
        return
      }
      logAction("web_search_trusted", selectedText)
      try {
        // Web search with trusted sources is handled by the existing trusted sources state
        // Send question with web search forced on, prepending context instruction
        if (onSendQuestion) {
          const enhancedQuestion = `collect more information regarding this statement: ${selectedText}`
          await onSendQuestion({ question: enhancedQuestion, forceWebSearch: true })
        }
      } catch (error) {
        logger.error("Failed to execute trusted web search", error as Error)
        toast.error("Search failed", {
          description: "Please try again",
        })
      }
    },
    [canUseWebSearch, logAction, onSendQuestion, sessionType]
  )

  const handleCreateDocument = useCallback(
    async (selectedText: string) => {
      logAction("create_document", selectedText)

      const normalizedContent = selectedText.trim()
      if (!normalizedContent) {
        toast.error("No content selected", {
          description: "Select text first, then create a document.",
        })
        return
      }

      const generatedTitle =
        normalizedContent.length > 72
          ? `${normalizedContent.slice(0, 69).trimEnd()}...`
          : normalizedContent

      try {
        const tokenResult = await getToken(instance)
        const created = await createDocumentApi(
          {
            title: generatedTitle,
            content: normalizedContent,
            contentFormat: "markdown",
            sourceSessionId: currentSessionId ?? undefined,
          },
          tokenResult?.accessToken
        )

        await queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
        void navigate(`/documents/${encodeURIComponent(created.id)}`)

        toast.success("Document created", {
          description: "Opened in the document editor.",
        })
      } catch (error) {
        logger.error("Failed to create document from selected text", error as Error)
        toast.error("Failed to create document", {
          description: "Please try again",
        })
      }
    },
    [currentSessionId, instance, logAction, navigate, queryClient]
  )

  const handleStartResearch = useCallback(
    async (selectedText: string) => {
      logAction("start_research", selectedText)
      try {
        // Start research with selected text as context
        if (onSendQuestion) {
          // First set the question so it appears in the chat
          if (onSetQuestion) {
            onSetQuestion(selectedText)
          }
          // Then trigger research
          await onSendQuestion({ question: selectedText, startResearch: true })
        }
      } catch (error) {
        logger.error("Failed to start research", error as Error)
        toast.error("Research failed", {
          description: "Please try again",
        })
      }
    },
    [logAction, onSendQuestion, onSetQuestion]
  )

  const handleFactCheck = useCallback(
    (selectedText: string) => {
      logAction("fact_check", selectedText)
      // Placeholder for fact-check feature
      toast.info("Fact-check feature coming soon", {
        description: "This feature is being developed in a separate branch.",
      })
    },
    [logAction]
  )

  const handleBreakOutChat = useCallback(
    async (selectedText: string, messageType: "user" | "assistant" = "user") => {
      logAction("break_out_chat", selectedText)

      // Validate content before proceeding
      if (!selectedText || selectedText.trim().length === 0) {
        logger.warn("Break out chat called with empty content")
        toast.error("No content to break out")
        return
      }

      logger.info("Break out chat starting", {
        contentLength: selectedText.length,
        messageType,
        sessionType,
        projectId,
      })

      try {
        // Get the current session to create a reference link
        const currentSession = chatService.getOpenSession(sessionType)
        const sourceSessionTitle = currentSession?.title || "previous chat"
        const sourceSessionId = currentSession?.id || currentSessionId

        logger.info("Creating breakout session", {
          sourceSessionId,
          sourceSessionTitle,
        })

        // Create a new chat session with project context if available
        logger.info("Calling chatService.createSession", {
          sessionType,
          titleHint: selectedText.substring(0, 100),
          projectId,
        })

        let newSession
        try {
          newSession = await chatService.createSession(
            sessionType,
            selectedText.substring(0, 100), // Use first 100 chars as title hint
            undefined,
            undefined,
            undefined,
            projectId,
            sourceSessionId ?? undefined, // Store source session ID for lineage tracking
            undefined // sourceMessageId - not available in current breakout implementation
          )
          logger.info("chatService.createSession returned", {
            newSession,
            hasId: !!newSession?.id,
            sessionId: newSession?.id,
          })
        } catch (createError) {
          logger.error("chatService.createSession threw error", {
            error: createError,
            message: (createError as Error).message,
            stack: (createError as Error).stack,
          })
          throw createError
        }

        if (!newSession?.id) {
          logger.error("Session creation returned no ID", { newSession })
          toast.error("Failed to create new chat")
          return
        }

        logger.info("Session created successfully", { newSessionId: newSession.id })

        // Optimistically add the new session to the cache
        queryClient.setQueryData(
          queryKeys.sessionHistory(sessionType, projectId),
          (oldSessions: ChatSessionModel[] | undefined) => {
            const sessions = oldSessions || []
            return [newSession, ...sessions]
          }
        )

        // Invalidate sessions query to ensure the session list is refreshed
        await queryClient.invalidateQueries({
          queryKey: queryKeys.sessionHistory(sessionType, projectId),
        })

        logger.info("Cache invalidated, navigating to new session")

        // Navigate to the new chat
        const chatPath = getRouteChatPath(sessionType)
        void navigate(`${chatPath}/${newSession.id}`)

        logger.info("Navigation complete, setting pending message after delay")

        // Set pending message AFTER navigation with a small delay to ensure
        // the new session page has mounted before the effect processes it
        setTimeout(() => {
          setPendingMessage({
            text: selectedText,
            sourceSessionId,
            sourceSessionTitle,
            messageType,
          })
          logger.info("Pending message set for new session")
        }, 150)

        // Success toast will be shown after message is populated
      } catch (error) {
        logger.error("Failed to break out chat", error as Error)
        toast.error("Failed to create new chat", {
          description: "Please try again",
        })
      }
    },
    [
      logAction,
      chatService,
      sessionType,
      currentSessionId,
      navigate,
      setPendingMessage,
      projectId,
      queryClient,
    ]
  )

  const handleAddToMemory = useCallback(
    async (selectedText: string) => {
      logAction("add_to_memory", selectedText)
      try {
        const tokenResult = await getToken(instance)
        if (prismMemory) {
          const result = await addPrismNoteApi(selectedText, currentSessionId ?? "", {
            idToken: tokenResult?.accessToken,
          })
          if (result.result !== "saved") {
            toast.info(t("personalization.memoryPanel.prism.notApplied"))
            return
          }
        } else {
          await addMemoryApi("stm", { text: selectedText }, tokenResult?.accessToken)
        }
        toast.success("Added to memory")
      } catch (error) {
        logger.error("Failed to add to memory", error as Error)
        toast.error("Failed to save memory", {
          description: "Please try again",
        })
      }
    },
    [logAction, instance, prismMemory, currentSessionId, t]
  )

  return {
    handleAskAbout,
    handleWebSearch,
    handleWebSearchTrusted,
    handleCreateDocument,
    handleStartResearch,
    handleFactCheck,
    handleBreakOutChat,
    handleAddToMemory,
  }
}
